/**
 * Theme Context Provider
 * 
 * This context provides theme management functionality including:
 * - Dark/light mode switching
 * - Theme persistence
 * - System theme detection
 * - Accessibility compliance
 * 
 * Features:
 * - Local storage persistence
 * - System theme detection
 * - Smooth transitions
 * - Accessibility considerations
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePreferences } from '../hooks/usePreferences';
import type { UserPreferences } from '../types/preference.types';
import { preferenceService } from '../services/preference-service';
import { preferenceKeys } from '../hooks/usePreferences';

type Theme = 'light' | 'dark' | 'system';
type PreferenceTheme = 'light' | 'dark' | 'auto';

// Map preference theme ('auto') to context theme ('system')
const mapPreferenceToTheme = (prefTheme: PreferenceTheme): Theme => {
  return prefTheme === 'auto' ? 'system' : prefTheme;
};

// Map context theme ('system') to preference theme ('auto')
const mapThemeToPreference = (theme: Theme): PreferenceTheme => {
  return theme === 'system' ? 'auto' : theme;
};

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  resolvedTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

/**
 * Theme Provider Component
 * 
 * Provides theme management functionality to the application.
 * Integrates with user preferences for theme persistence.
 */
export function ThemeProvider({ 
  children, 
  defaultTheme = 'system',
  storageKey = 'moneymojo-theme'
}: ThemeProviderProps): React.ReactElement {
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('light');
  const { data: preferences } = usePreferences();
  const queryClient = useQueryClient();

  // Get system theme preference
  const getSystemTheme = (): 'light' | 'dark' => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  };

  // Resolve theme based on current setting
  const resolveTheme = (currentTheme: Theme): 'light' | 'dark' => {
    if (currentTheme === 'system') {
      return getSystemTheme();
    }
    return currentTheme;
  };

  // Load theme from preferences first, then localStorage as fallback
  useEffect(() => {
    const userPrefs = preferences as UserPreferences | undefined;
    if (userPrefs?.theme) {
      // Load from user preferences (source of truth)
      const prefTheme = mapPreferenceToTheme(userPrefs.theme);
      setTheme(prefTheme);
      // Sync localStorage
      localStorage.setItem(storageKey, prefTheme);
    } else {
      // Fallback to localStorage if preferences not loaded yet
      const storedTheme = localStorage.getItem(storageKey) as Theme;
      if (storedTheme && ['light', 'dark', 'system'].includes(storedTheme)) {
        setTheme(storedTheme);
      }
    }
  }, [preferences, storageKey]);

  // Update resolved theme when theme changes
  useEffect(() => {
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    
    // Apply theme to document
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolved);
    
    // Update meta theme-color
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', resolved === 'dark' ? '#111827' : '#ffffff');
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme === 'system' && typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        const resolved = resolveTheme(theme);
        setResolvedTheme(resolved);
        
        const root = document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(resolved);
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  // Save theme to localStorage and preferences
  const handleSetTheme = async (newTheme: Theme) => {
    setTheme(newTheme);
    localStorage.setItem(storageKey, newTheme);
    
    // Update preferences in database (non-blocking)
    try {
      const prefTheme = mapThemeToPreference(newTheme);
      const result = await preferenceService.updatePreferences({ theme: prefTheme });
      
      // If update successful, invalidate preferences query cache so settings page updates
      if (result.data) {
        queryClient.setQueryData(preferenceKeys.current(), (oldData: any) => {
          if (oldData) {
            return { ...oldData, theme: prefTheme };
          }
          return oldData;
        });
      }
    } catch (error) {
      // Silently fail - localStorage is already updated, preferences will sync on next load
      console.warn('Failed to update theme preference:', error);
    }
  };

  // Toggle between light and dark (skip system)
  const toggleTheme = () => {
    const newTheme = resolvedTheme === 'light' ? 'dark' : 'light';
    handleSetTheme(newTheme);
  };

  const value: ThemeContextType = {
    theme,
    setTheme: handleSetTheme,
    toggleTheme,
    resolvedTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to use theme context
 */
export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
